import React, { useState } from 'react';
import { Target } from 'lucide-react';
import {
  Card,
  CardContent,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import type { OrderResponse } from '../../services/api/types.gen';
import type { DisciplineType } from '../../services/api/models/DisciplineType';
import { orderGoalCapabilities } from '../../utils/goalTaskCapabilities';
import { GOAL_DISCIPLINE_OPTIONS, isGoalDiscipline } from '../../utils/goals';
import GoalsTab, { type GoalsTabVisit } from './GoalsTab';

interface OrderGoalsCardProps {
  order: OrderResponse;
  /** All-false write capabilities when the order is read only (e.g. deleted). */
  isReadonly?: boolean;
}

/**
 * Goals card for an allowlisted order's detail panel (EMR-1538). Rendered
 * below the order form as a sibling `Card`, not a tab — the order detail
 * panel is a single scrolling column. Owns its own discipline selection and
 * loading state entirely independently of `GenericOrderForm`'s autosave: it
 * never touches `order_data_last_updated` or the panel's save-status pill.
 *
 * Only the actions the backend allows from an order render here: add goal,
 * add intervention, complete an already-performed intervention, and push a
 * goal to the next episode. `orderGoalCapabilities` is the single source for
 * that gating — see `utils/goalTaskCapabilities.ts`.
 */
export const OrderGoalsCard: React.FC<OrderGoalsCardProps> = ({
  order,
  isReadonly = false,
}) => {
  // Defaults to the order's own discipline (EMR-1538) — a physician order can
  // legitimately be raised for PT/OT/etc, not just SN. Falls back to SN when
  // the order has no discipline set or one that carries no goals (MSW, HHA).
  // The parent keys this component by `order.id` (see OrderDetailPanel), so
  // switching orders remounts it and re-reads this initial value instead of
  // carrying over a stale selection.
  const [discipline, setDiscipline] = useState<DisciplineType>(
    isGoalDiscipline(order.discipline) ? order.discipline : 'SN'
  );

  if (!order.supports_goals) return null;

  const liveCapabilities = orderGoalCapabilities(order);
  // A read-only mount (deleted order, viewed from the deleted-tasks list)
  // shows the same goals but offers no write controls, regardless of status
  // — matching how OrderDetailPanel treats `isDeleted` for the order form.
  const capabilities = isReadonly
    ? {
        ...liveCapabilities,
        canAddGoals: false,
        canAddInterventions: false,
        canCompleteInterventions: false,
        canPushGoals: false,
      }
    : liveCapabilities;

  const goalsVisit: GoalsTabVisit = {
    id: order.id,
    // `visit_type` is a loosely-typed string on the wire (TaskListResponse);
    // GoalsTab never uses it to re-derive gating (that's `capabilities`,
    // supplied by the caller), only as a fallback label, so the order's own
    // type id is a safe value to carry here.
    visit_type: order.order_type,
    status: order.status,
    episode_id: order.episode_id,
    episode_start_date: order.episode_start_date,
    episode_end_date: order.episode_end_date,
  };

  return (
    <Card className='mt-4'>
      <CardContent className='space-y-3 p-4'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <span className='flex items-center gap-1.5 text-sm font-medium text-muted-foreground'>
            <Target className='h-4 w-4' />
            Goals
          </span>
          <div className='w-full sm:w-48'>
            <Label htmlFor='order-goals-discipline' className='sr-only'>
              Discipline
            </Label>
            <Select
              value={discipline}
              onValueChange={val => setDiscipline(val as DisciplineType)}
            >
              <SelectTrigger
                id='order-goals-discipline'
                selectSize='sm'
                className='w-full sm:w-48'
              >
                <SelectValue placeholder='Select discipline' />
              </SelectTrigger>
              <SelectContent>
                {GOAL_DISCIPLINE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <GoalsTab
          key={order.id}
          open
          onClose={() => {}}
          patientFirstName=''
          patientLastName=''
          visit={goalsVisit}
          inline
          capabilities={capabilities}
          discipline={discipline}
        />
      </CardContent>
    </Card>
  );
};

export default OrderGoalsCard;
